import { parsePhoneNumberWithError } from 'libphonenumber-js';
import type { DefaultValues } from 'react-hook-form';
import { z } from 'zod';
import { IAddress, IDatePicker } from '@bridebook/models/source/models/Weddings.types';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { PARTNER_NAME_MAX_LENGTH } from '@bridebook/toolbox/src/validation/partner-names/const';
import { createPartnerNameSchema } from '@bridebook/web/lib/validation/partner-names/schema';
import { weddingDateSchema } from '@bridebook/web/lib/datepicker/schema';
import { EnquiryIntentCheckboxes, type EnquiryIntents } from '@bridebook/web/lib/enquiries/intent';
import type { IUserSerialized } from '@bridebook/web/lib/users/types';
import type { IProfile } from '@bridebook/web/lib/weddings/types';
import { ILeadIntentOption } from '@bridebook/models/source/models/Suppliers/Leads.types';
import {
  ENQUIRY_DETAILS,
  type EnquiryDetail,
} from '@bridebook/web/lib/enquiries/enquiry-readiness';

export const UNDECIDED_DATE: IDatePicker = { y: null, m: null, d: null };

const locationSchema = z.custom<IAddress>((val) => typeof val === 'object' && val !== null);

const intentSchema = z.object({
  brochure: z.boolean(),
  pricing: z.boolean(),
  availability: z.boolean(),
  showround: z.boolean(),
  moreAvailability: z.boolean(),
  other: z.boolean(),
});

const createAtLeastOneIntentSchema = (
  options: readonly ILeadIntentOption[],
  message = getI18n().t('enquiries:error.chooseAtLeastOneOption'),
) =>
  intentSchema.refine(
    (intent) => options.some((key) => intent[key]),
    () => ({
      message,
    }),
  );

export const isPossiblePhone = (value: string): boolean => {
  try {
    return parsePhoneNumberWithError(value).isPossible();
  } catch {
    return false;
  }
};

const phoneSchema = z
  .string()
  .refine(isPossiblePhone, () => ({ message: getI18n().t('common:validation.invalid.phone') }));

const createEnquiryFormBaseSchema = () => {
  const partnerNameSchema = createPartnerNameSchema({ allowEmpty: false });
  return z.object({
    email: z.string().email(getI18n().t('error.fillOutMissingFields')),
    phone: phoneSchema,
    partners: z.tuple([partnerNameSchema, partnerNameSchema]),
    weddingDate: weddingDateSchema.nullable(),
    guestsInitialTarget: z.number().min(1, getI18n().t('error.fillOutMissingFields')),
    location: locationSchema.nullable(),
    message: z.string(),
    intent: intentSchema,
  });
};

// Static instance for type inference; its messages are never shown to users.
const enquiryFormBaseSchema = createEnquiryFormBaseSchema();
export type EnquiryFormValues = z.infer<typeof enquiryFormBaseSchema>;

/**
 * Send rules of the enquiry form: guests and wedding date optional, message and at least one
 * displayed intent required, location required for non-venues.
 */
export const createEnquiryFormSendSchema = (isVenue: boolean) =>
  enquiryFormBaseSchema.extend({
    partners: z.tuple([
      createPartnerNameSchema({
        allowEmpty: false,
        messages: {
          required: getI18n().t('enquiries:v2.form.partners.0.error.required'),
          tooLong: getI18n().t('enquiries:v2.form.partners.0.error.tooLong', {
            maxLength: PARTNER_NAME_MAX_LENGTH,
          }),
          containsNumbers: getI18n().t('enquiries:v2.form.partners.0.error.containsNumbers'),
          containsInvalidCharacters: getI18n().t(
            'enquiries:v2.form.partners.0.error.containsInvalidCharacters',
          ),
          invalid: getI18n().t('enquiries:v2.form.partners.0.error.invalid'),
        },
      }),
      createPartnerNameSchema({
        allowEmpty: false,
        messages: {
          required: getI18n().t('enquiries:v2.form.partners.1.error.required'),
          tooLong: getI18n().t('enquiries:v2.form.partners.1.error.tooLong', {
            maxLength: PARTNER_NAME_MAX_LENGTH,
          }),
          containsNumbers: getI18n().t('enquiries:v2.form.partners.1.error.containsNumbers'),
          containsInvalidCharacters: getI18n().t(
            'enquiries:v2.form.partners.1.error.containsInvalidCharacters',
          ),
          invalid: getI18n().t('enquiries:v2.form.partners.1.error.invalid'),
        },
      }),
    ]),
    email: z.string().email(getI18n().t('enquiries:v2.form.email.error.invalid')),
    phone: z.string().refine(isPossiblePhone, () => ({
      message: getI18n().t('enquiries:v2.form.phone.error.invalid'),
    })),
    message: z.string().min(1, {
      message: getI18n().t('enquiries:v2.form.message.error.required'),
    }),
    weddingDate: weddingDateSchema.nullable(),
    location: isVenue
      ? locationSchema.nullable()
      : locationSchema.nullable().refine(
          (val): val is IAddress => val !== null,
          () => ({
            message: getI18n().t('enquiries:v2.form.location.error.required'),
          }),
        ),
    // Any lead intent counts: direct sends such as the showround scheduler set intents the form has no pill for.
    intent: createAtLeastOneIntentSchema(
      Object.values(ILeadIntentOption),
      getI18n().t('enquiries:v2.form.intent.error.required'),
    ),
    guestsInitialTarget: z.number().min(0),
  });

// Edit screen save: the send rules without message and intent.
export const createEnquiryFormEditSchema = (isVenue: boolean) =>
  createEnquiryFormSendSchema(isVenue).omit({ message: true, intent: true });

/**
 * RHF {@link DefaultValues} deep-partials nested objects; keep wedding `location` as {@link IAddress}.
 */
export type EnquiryFormDefaultValues = Omit<DefaultValues<EnquiryFormValues>, 'location'> & {
  location: IAddress | null;
};

export const deriveInitialIntent = (defaultIntent?: keyof EnquiryIntents): EnquiryIntents => {
  if (!defaultIntent) return { ...EnquiryIntentCheckboxes };
  return { ...EnquiryIntentCheckboxes, brochure: false, [defaultIntent]: true };
};

// The form's values as the couple's record fills them.
export const deriveInitialValues = (
  profile: IProfile,
  user: IUserSerialized | null | undefined,
  defaultIntent?: keyof EnquiryIntents,
  defaultMessage = '',
): EnquiryFormDefaultValues => ({
  email: user?.contacts?.email ?? '',
  phone: user?.contacts?.phone ?? '',
  partners: [profile.partners?.[0] ?? '', profile.partners?.[1] ?? ''],
  weddingDate: profile.date ?? null,
  guestsInitialTarget: profile.guests.estimate ?? 0,
  location: profile.location ?? null,
  message: defaultMessage,
  intent: deriveInitialIntent(defaultIntent),
});

const DETAIL_OF_FIELD: Partial<Record<keyof EnquiryFormValues, EnquiryDetail>> = {
  partners: 'partnerNames',
  email: 'email',
  phone: 'phone',
};

/**
 * The details a venue enquiry still lacks, by the edit screen's own rules, so
 * a record that passes here passes the form. Takes the form's default values,
 * which the schema parses. Checked as a venue: location is the one field a
 * venue never needs.
 */
let venueEditSchema: ReturnType<typeof createEnquiryFormEditSchema> | undefined;
export const missingEnquiryDetails = (values: unknown): EnquiryDetail[] => {
  // Built once: its messages are never shown, so the language at build time does not matter.
  venueEditSchema ??= createEnquiryFormEditSchema(true);
  const result = venueEditSchema.safeParse(values);
  if (result.success) return [];
  const failed = new Set(
    result.error.issues.map((issue) => DETAIL_OF_FIELD[issue.path[0] as keyof EnquiryFormValues]),
  );
  return ENQUIRY_DETAILS.filter((detail) => failed.has(detail));
};
